import { FC, ReactNode } from 'react';
import { useTranslation } from '@hooks/useTranslation';

interface ReportSectionProps {
  title: string;
  /** Number of rendered rows. Shown as "Title (n)"; a count of 0 renders nothing (story-206). */
  count?: number;
  truncated?: boolean;
  children: ReactNode;
}

/** One titled section. With a `count` it follows the email's "Incidents (3)" header and collapses
 * (renders nothing) when the count is 0, so no card ever shows an empty heading. Without a `count`
 * it always renders; the `null`-hiding rule (section absent for the viewer's role) stays with the
 * parent, which simply does not mount it. */
export const ReportSection: FC<ReportSectionProps> = ({ title, count, truncated, children }) => {
  const t = useTranslation('dailySiteReport');
  if (count === 0) return null;
  return (
    <div className="mb-4 last:mb-0" data-testid="report-section">
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        {count === undefined ? title : `${title} (${count})`}
      </h3>
      <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2">{children}</div>
      {truncated && <p className="text-xs text-gray-500 mt-2">{t.truncatedNote.replace('{n}', '100')}</p>}
    </div>
  );
};

export default ReportSection;
